/**
 * The Inbox client — `tasks/inbox.md` §1.
 *
 * ── WHY THIS IS NOT SHAPED LIKE `ResultsService` ─────────────────────────────────────
 *
 * `ResultsService` loads EVERY lab order in the practice (500 a page, every page) into a
 * `BehaviorSubject`, and then `results-list.component.ts` splits it into five tabs, filters it and
 * counts it in memory — and `sidenav.component.ts` subscribes to the same array and counts it
 * again for the badge. That is the shape `tasks/prod-slowdown-db-ceiling.md` records the cost of,
 * and this service deliberately does not repeat it.
 *
 * Instead: ONE tab's worth of rows at a time, keyset-paged from the server, plus a separate
 * `/counts` call that is four `COUNT(*)`s and feeds both the tab labels and the sidenav badge. The
 * badge therefore costs one small request per group change, not the whole practice.
 *
 * ── STATE IS PER TAB, AND A TAB IS ONLY LOADED ONCE IT IS LOOKED AT ──────────────────
 *
 * `items$(tab)` is a `BehaviorSubject` per tab, created on demand. Nothing is fetched until a tab
 * is opened, and switching back to a tab already loaded draws immediately from the held rows while
 * the refresh runs. Four tabs eagerly loaded on page open would be four round trips to draw one.
 *
 * ── THE FIVE SOCKET EVENTS ALREADY EXIST ─────────────────────────────────────────────
 *
 * `patientDocumentRequested`, `patientDocumentReturned`, `patientDocumentRevoked`,
 * `patientDocumentReviewed` and `patientDocumentValidated` are emitted to the group room today by
 * the document controller and `utils/patientDocumentRequestUtils.js`. NO NEW SERVER EVENT WAS
 * ADDED for the Inbox, and none should be: every one of them means "an item may have changed tab",
 * which is all this service needs to know.
 *
 * It RELOADS rather than patching, which is the opposite of what
 * `patient-document-request.service.ts` does with the same events — and the difference is the
 * point. That service holds one patient's rows and a patch keeps an open detail row intact. This
 * one holds a TAB, and every one of those events can move an item OUT of the tab being viewed. A
 * patch would leave a signed-off document sitting in "New" until the page was reloaded, which is
 * the one thing a worklist must never do.
 *
 * A future source's events are added to `INBOX_RELOAD_EVENTS` (per item type, in
 * `inbox-item.model.ts`) and nothing else changes.
 */

import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, catchError, of, switchMap, tap } from 'rxjs';

import {
  InboxCounts,
  InboxItemDto,
  InboxPage,
  InboxTab,
  INBOX_TABS,
  INBOX_RELOAD_EVENTS,
  inboxReloadEvents,
  isTaskEventRelevant,
} from '../models/inbox-item.model';
import { GroupService } from './group.service';
import { SocketService } from './socket.service';
import { UserService } from './user.service';

/** Everything a tab currently holds. One of these per tab, in `tabState`. */
export interface InboxTabState {
  items: InboxItemDto[];
  /** Opaque. Null means "no more pages" — see `tasks/inbox.md` §3.3. */
  nextCursor: string | null;
  hasMore: boolean;
  loading: boolean;
  /** The last page's envelope, for the search hint. Null before the first load. */
  page: InboxPage | null;
}

const EMPTY_TAB: InboxTabState = {
  items: [], nextCursor: null, hasMore: false, loading: false, page: null,
};

const ZERO_COUNTS: InboxCounts = { 'new': 0, 'for-review': 0, flagged: 0, pending: 0 };

/**
 * The group-room events that mean "an item may have moved", across every item type — declared per type
 * in `INBOX_RELOAD_EVENTS`. See the header for why every one of them RELOADS the open tab rather than
 * patching a row.
 */
const RELOAD_EVENTS = inboxReloadEvents();
/** Task events are filtered per viewer before they reload anything — see `isTaskEventRelevant`. */
const TASK_EVENTS = new Set(INBOX_RELOAD_EVENTS['task'] || []);

@Injectable({ providedIn: 'root' })
export class InboxService {
  private apiUrl = 'api/inbox';

  private countsSubject = new BehaviorSubject<InboxCounts>(ZERO_COUNTS);
  /** Four integers. Read by the tab labels AND by the sidenav badge. */
  counts$ = this.countsSubject.asObservable();

  private tabState = new Map<InboxTab, BehaviorSubject<InboxTabState>>();

  groupId: string | null = null;

  /**
   * The tab the page is currently showing, so a socket event knows which one to reload.
   *
   * Null when the Inbox is not open — and then an event refreshes the COUNTS ONLY. The badge has to
   * stay honest whichever page the user is on; the rows do not, because nobody is looking at them
   * and the tab reloads when it is next opened.
   */
  private activeTab: InboxTab | null = null;

  /** Who is looking, for `isTaskEventRelevant`. */
  private viewer: { id?: string | null; userType?: string | null } | null = null;

  constructor(
    private http: HttpClient,
    private groupService: GroupService,
    private socketService: SocketService,
    private userService: UserService,
  ) {
    this.userService.userProfile$.subscribe((user: any) => {
      this.viewer = user ? { id: user.id ?? null, userType: user.userType ?? null } : null;
    });
    this.setupRealTimeUpdates();

    this.groupService.selectedGroup$.pipe(
      switchMap((selectedGroup: any) => {
        if (!selectedGroup) {
          this.groupId = null;
          this.resetAll();
          return EMPTY;
        }
        this.groupId = selectedGroup.id;
        // A different practice's rows must never be drawn under the new group's tabs even for one
        // frame, so everything is cleared BEFORE the first request goes out.
        this.resetAll();

        /*
         * THE OPEN TAB'S ROWS, RE-REQUESTED NOW THAT THERE IS A GROUP TO ASK ABOUT.
         *
         * Without this the Inbox opened EMPTY on a page refresh and stayed empty until the user
         * clicked another tab and back — while the counts above it said there was work waiting.
         *
         * The race: `groupId` is set here, asynchronously. The page's `ngOnInit` calls `refresh()`
         * immediately, and `load()` begins `if (!this.groupId) return of(null)` — a silent no-op
         * with nothing to retry it. `loadCounts()` is issued from inside THIS pipeline, so it
         * always had a group and always worked, which is precisely why the counts looked right and
         * the list looked empty. It read as a rendering fault and was a data fault.
         *
         * Fired in parallel with the counts rather than chained after them: they are independent
         * requests and the rows are what the user is waiting to see.
         *
         * This also covers switching practice with the Inbox open, which had the same blank list
         * for the same reason, and `resetAll()` above had just guaranteed it.
         */
        if (this.activeTab) this.load(this.activeTab).subscribe();

        return this.loadCounts();
      }),
    ).subscribe();
  }

  /* ── State ──────────────────────────────────────────────────────────────────────────── */

  private subjectFor(tab: InboxTab): BehaviorSubject<InboxTabState> {
    let subject = this.tabState.get(tab);
    if (!subject) {
      subject = new BehaviorSubject<InboxTabState>({ ...EMPTY_TAB });
      this.tabState.set(tab, subject);
    }
    return subject;
  }

  /** One tab's rows, paging state and loading flag. Created on demand; nothing is fetched here. */
  state$(tab: InboxTab): Observable<InboxTabState> {
    return this.subjectFor(tab).asObservable();
  }

  stateOf(tab: InboxTab): InboxTabState {
    return this.subjectFor(tab).getValue();
  }

  private patch(tab: InboxTab, patch: Partial<InboxTabState>): void {
    const subject = this.subjectFor(tab);
    subject.next({ ...subject.getValue(), ...patch });
  }

  private resetAll(): void {
    for (const tab of INBOX_TABS) this.subjectFor(tab).next({ ...EMPTY_TAB });
    this.countsSubject.next(ZERO_COUNTS);
  }

  /** Told by the page, so a socket event knows which tab to reload. */
  setActiveTab(tab: InboxTab | null): void {
    this.activeTab = tab;
  }

  /* ── Loading ────────────────────────────────────────────────────────────────────────── */

  /**
   * First page of a tab, replacing whatever it held.
   *
   * `search` is passed to the server, which matches it against the PLAINTEXT columns only —
   * patient names are encrypted with no blind index. The page filters names within the loaded rows
   * and says so. See `inboxSearchHint`.
   *
   * A failed load leaves the rows ALONE rather than blanking a populated tab: a dropped connection
   * on a worklist should not look like an empty worklist.
   */
  load(tab: InboxTab, search: string = ''): Observable<InboxPage | null> {
    if (!this.groupId) return of(null);
    this.patch(tab, { loading: true });

    return this.fetch(tab, search, null).pipe(
      tap((page) => {
        if (!page) { this.patch(tab, { loading: false }); return; }
        this.patch(tab, {
          items: page.data || [],
          nextCursor: page.nextCursor || null,
          hasMore: !!page.hasMore,
          loading: false,
          page,
        });
      }),
    );
  }

  /**
   * The NEXT page, appended.
   *
   * Refuses when there is nothing more, and while a load is already in flight — a scroll handler
   * fires far faster than a round trip, and two concurrent loads of the same cursor would append
   * the same rows twice.
   */
  loadMore(tab: InboxTab, search: string = ''): Observable<InboxPage | null> {
    const state = this.stateOf(tab);
    if (!this.groupId || !state.hasMore || !state.nextCursor || state.loading) return of(null);
    this.patch(tab, { loading: true });

    return this.fetch(tab, search, state.nextCursor).pipe(
      tap((page) => {
        if (!page) { this.patch(tab, { loading: false }); return; }
        const current = this.stateOf(tab);
        this.patch(tab, {
          // Appended, and de-duplicated on `itemType:sourceId`. The keyset cursor should never
          // serve a row twice — `verify-inbox.js` asserts exactly that across a tie — but a list
          // that renders a duplicate is a list that throws in `trackBy`, and the guard is one line.
          items: dedupe([...current.items, ...(page.data || [])]),
          nextCursor: page.nextCursor || null,
          hasMore: !!page.hasMore,
          loading: false,
          page,
        });
      }),
    );
  }

  private fetch(tab: InboxTab, search: string, cursor: string | null): Observable<InboxPage | null> {
    let params = new HttpParams().set('tab', tab);
    if (cursor) params = params.set('cursor', cursor);
    if (search && search.trim()) params = params.set('q', search.trim());

    return this.http.get<InboxPage>(`${this.apiUrl}/group/${this.groupId}`,
      { ...this.authOptions(), params })
      .pipe(catchError(() => of(null)));
  }

  /**
   * The four tab sizes.
   *
   * Four `COUNT(*)`s, and the ONLY thing the sidenav badge subscribes to. A failure leaves the last
   * known counts alone rather than zeroing the badge — a badge that reads 0 because a request
   * failed tells the practice there is no work waiting, which is the worst thing it could say.
   */
  loadCounts(): Observable<InboxCounts | null> {
    if (!this.groupId) return of(null);
    return this.http.get<{ success: boolean; data: InboxCounts }>(
      `${this.apiUrl}/group/${this.groupId}/counts`, this.authOptions())
      .pipe(
        tap((result) => {
          if (result && result.data) this.countsSubject.next(result.data);
        }),
        catchError(() => of(null)),
        switchMap(() => of(this.countsSubject.getValue())),
      );
  }

  /* ── Flagging ───────────────────────────────────────────────────────────────────────── */

  /**
   * Flag an item, or update the note on one already flagged.
   *
   * The held row is patched OPTIMISTICALLY from the response so the flag icon appears without a
   * reload — the item has not changed tab, so there is nothing to re-page. `flagged` itself IS
   * reloaded, because the item has just entered or left it.
   */
  flag(itemType: string, sourceId: string, note?: string | null): Observable<any> {
    if (!this.groupId) return of(null);
    return this.http.post<any>(
      `${this.apiUrl}/group/${this.groupId}/${itemType}/${sourceId}/flag`,
      { note: note || null }, this.authOptions())
      .pipe(tap((result) => {
        if (!result || !result.success) return;
        this.patchItem(itemType, sourceId, {
          flaggedAt: result.data ? result.data.flaggedAt : new Date().toISOString(),
          flagNote: result.data ? result.data.flagNote : (note || null),
        });
        this.afterFlagChange();
      }));
  }

  unflag(itemType: string, sourceId: string): Observable<any> {
    if (!this.groupId) return of(null);
    return this.http.post<any>(
      `${this.apiUrl}/group/${this.groupId}/${itemType}/${sourceId}/unflag`,
      {}, this.authOptions())
      .pipe(tap((result) => {
        if (!result || !result.success) return;
        this.patchItem(itemType, sourceId, { flaggedAt: null, flagNote: null, flaggedByName: null });
        this.afterFlagChange();
      }));
  }

  /**
   * The Flagged tab is now wrong and the counts are now wrong; every other tab is unaffected,
   * because flagging is an ANNOTATION and not a move.
   */
  private afterFlagChange(): void {
    this.loadCounts().subscribe();
    if (this.activeTab === 'flagged') {
      this.load('flagged').subscribe();
    } else {
      this.subjectFor('flagged').next({ ...EMPTY_TAB });
    }
  }

  /** Source ids of one type held in any tab, lower-cased. */
  private loadedSourceIds(itemType: string): Set<string> {
    const ids = new Set<string>();
    for (const tab of INBOX_TABS) {
      for (const item of this.stateOf(tab).items) {
        if (item.itemType === itemType) ids.add(String(item.sourceId).toLowerCase());
      }
    }
    return ids;
  }

  /** Patches one row wherever it is held, across every tab. */
  private patchItem(itemType: string, sourceId: string, patch: Partial<InboxItemDto>): void {
    for (const tab of INBOX_TABS) {
      const state = this.stateOf(tab);
      let touched = false;
      const items = state.items.map((item) => {
        if (item.itemType !== itemType || item.sourceId !== sourceId) return item;
        touched = true;
        return { ...item, ...patch };
      });
      if (touched) this.patch(tab, { items });
    }
  }

  /* ── Real time ──────────────────────────────────────────────────────────────────────── */

  /**
   * Every event means "an item may have moved tab", so the OPEN tab reloads and the counts reload.
   * See the header for why this reloads where the patient-file service patches.
   *
   * The group check is on `groupId` where the payload carries one. Two of these events do not, so
   * the reload is unconditional there — a reload of the caller's own group is at worst a wasted
   * request, whereas skipping one leaves a stale worklist.
   */
  private setupRealTimeUpdates(): void {
    for (const event of RELOAD_EVENTS) {
      this.socketService.listen(event, 'inbox-service').subscribe((payload: any) => {
        if (!this.groupId) return;
        if (payload && payload.groupId && payload.groupId !== this.groupId) return;
        if (TASK_EVENTS.has(event) && !isTaskEventRelevant(payload, this.viewer, this.loadedSourceIds('task'))) return;

        this.loadCounts().subscribe();
        if (this.activeTab) this.load(this.activeTab).subscribe();
        // Every OTHER tab is now possibly stale. Rather than reloading four tabs nobody is looking
        // at, they are emptied so the next visit fetches — the same decision as `afterFlagChange`.
        for (const tab of INBOX_TABS) {
          if (tab !== this.activeTab) this.subjectFor(tab).next({ ...EMPTY_TAB });
        }
      });
    }
  }

  private authOptions() {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  }
}

/** De-duplicates on the composite key. Exported-shaped helper kept module-local; see `loadMore`. */
function dedupe(items: InboxItemDto[]): InboxItemDto[] {
  const seen = new Set<string>();
  const kept: InboxItemDto[] = [];
  for (const item of items) {
    const key = `${item.itemType}:${item.sourceId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    kept.push(item);
  }
  return kept;
}
